// Services

// Constants
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'

// Utils
import { getUserIp } from '@src/quince-site-common/src/services/ipService'
import { getUa } from '@src/quince-site-common/src/services/uaService'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackGAEvent } from '@src/telemetry/track'

import { handleSgtmApi } from '../handleSgtmApi'

let isFetching = false
let eventQueue: Record<string, any>[] = []

const trackWithSgtm = async ({ eventData }: { eventData: Record<string, any> }) => {
  const ip = getCookie(ANALYTIC_COOKIES.ECOMM_IP)
  const ua = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.ECOMM_UA, false) ?? undefined
  const shouldFetchDependencies = !ip || !ua

  if (shouldFetchDependencies) {
    pushToEventQueue(eventData)
    if (!isFetching) {
      isFetching = true
      const { areAllDependenciesLoaded } = await fetchDependencies({ ip, ua })
      isFetching = false
      if (areAllDependenciesLoaded) {
        flushEventQueue()
      }
    }
  } else {
    flushEventQueue()
    trackEvent(eventData, ip, ua)
  }
}

export const trackEvent = (eventData: Record<string, any>, ip?: string, ua?: string) => {
  const enhancedEventData = {
    ...eventData,
    user_agent: ua,
    ip,
  }
  trackGAEvent(TELEMETRY_EVENT_NAMES.INTERNAL_ANALYTICS, enhancedEventData, false)
  handleSgtmApi(enhancedEventData, 'internal_analytics_sgtm_api', 'internal_analytics_properties')
}

export const pushToEventQueue = (eventData: Record<string, any>) => {
  eventQueue.push(eventData)
}

export const flushEventQueue = () => {
  const ip = getCookie(ANALYTIC_COOKIES.ECOMM_IP)
  const ua = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.ECOMM_UA, false) ?? undefined

  const eventQueueCp: Record<string, any>[] = [...eventQueue]
  eventQueue = []
  eventQueueCp.forEach(ed => {
    trackEvent(ed, ip, ua)
  })
}

export const fetchDependencies = async ({ ip, ua }: { ip?: string; ua?: string }) => {
  const promiseMap: Record<string, () => Promise<any>> = {}

  if (!ip) {
    promiseMap.ip = getUserIp
  }
  if (!ua) {
    promiseMap.ua = getUa
  }

  const promiseKeys = Object.keys(promiseMap)
  const promisesArr = promiseKeys.map(key => promiseMap[key]?.()).filter(prm => prm !== undefined)
  const promiseResults = await Promise.allSettled(promisesArr)
  const areAllDependenciesLoaded = promiseResults.every(result => result.status === 'fulfilled')

  promiseResults.forEach((promiseResult, idx) => {
    const key = promiseKeys[idx]
    if (promiseResult.status === 'rejected') {
      captureFatalException(`Failed to fetch dependencies to send data to internal analytics for the key : ${key}`, {
        module: MODULES.INTERNAL_ANALYTICS,
        feature: FEATURES.TRACK_INTERNAL_ANALYTICS,
        extraInfo: promiseResult.reason,
      })
    }
  })

  return { areAllDependenciesLoaded }
}

export const getEventQueue = () => {
  return eventQueue
}

export const getIsFetching = () => {
  return isFetching
}

export const resetEventQueue = () => {
  eventQueue = []
}

export const resetIsFetching = () => {
  isFetching = false
}

export default trackWithSgtm
